import React, { useState, useEffect } from "react";
import "./PopupReport.scss";
import { generateReport } from "../../api/reportService";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchDashboardData } from "../../redux/actions/dashboardActions";
import { setIsGeneratingReport } from "../../redux/actions/reportActions";

const PopupReport = ({ dashboardData, rowSelection, onClose }) => {
  const dispatch = useDispatch();
  const isGeneratingReport = useSelector(
    (state) => state.report.isGeneratingReport,
  );

  const formatOptionsLabel = (d) => {
    const base = `${d.racetrack} - ${new Date(d.date).toLocaleDateString()} - ${d.vehicle} - ${d.best_lap_time}`;
    return d.filename ? `${base} - ${d.filename}` : base;
  };

  const computeLaps = (laps, session) => {
    if (!session) return [];
  
    return laps.map((lapTime, index) => ({
      value: index,
      label: lapTime,
      isBest: index === session.best_lap_idx,
      isValid: session.is_valid_lap?.[index],
    }));
  };
  
  const options1 = dashboardData.sessions.map((d, index) => ({
    value: formatOptionsLabel(d),
    key: `${d.session_id}-${index}`,
    sessionId: d.session_id,
  }));
  
  const options2 = [...options1]; // Same structure

  const [selectedOption1, setSelectedOption1] = useState("");
  const [selectedOption2, setSelectedOption2] = useState("");
  const [sessionId1, setsessionId1] = useState(null);
  const [sessionId2, setsessionId2] = useState(null);
  const [sessionLapId1, setSessionLapId1] = useState(null);
  const [sessionLapId2, setSessionLapId2] = useState(null);
  const [laps1, setLaps1] = useState([]);
  const [laps2, setLaps2] = useState([]);

  const session1 = dashboardData.sessions.find((s) => s.session_id === sessionId1);
  const session2 = dashboardData.sessions.find((s) => s.session_id === sessionId2);
  const computedLaps1 = computeLaps(laps1, session1);
  const computedLaps2 = computeLaps(laps2, session2);
  
  useEffect(() => {
    const sessionIds = Object.keys(rowSelection);
    const sessions = dashboardData.sessions;
  
    if (sessionIds.length === 2) {
      const selectedId1 = sessionIds[0];
      const selectedId2 = sessionIds[1];
  
      const option1 = options1.find((option) => option.sessionId === selectedId1);
      const option2 = options2.find((option) => option.sessionId === selectedId2);

      const session1 = sessions.find((s) => s.session_id === selectedId1);
      const session2 = sessions.find((s) => s.session_id === selectedId2);

      
      if (!selectedOption1 && option1) {
        setSelectedOption1(option1.value);
        setsessionId1(option1.sessionId);
        setLaps1(session1?.lap_times || []);
        setSessionLapId1(session1?.best_lap_idx || 0);
      }
      if (!selectedOption2 && option2) {
        setSelectedOption2(option2.value);
        setsessionId2(option2.sessionId);
        setLaps2(session2?.lap_times || []);
        setSessionLapId2(session2?.best_lap_idx || 0);
      }
    } else if (
      sessionIds.length === 0 ||
      sessionIds.length === 1 ||
      sessionIds.length >= 3
    ) {
      // Caso 0, 1 o >=3 filas seleccionadas: Selección libre con preselección similar a 1 fila seleccionada
      if (options1.length > 0 && options2.length > 0) {
        const defaultOption1 = options1[0]; // Primera opción por defecto
        const defaultOption2 = options2[1] || options1[0]; // Segunda opción por defecto

        const session1 = sessions.find((s) => s.session_id === defaultOption1.sessionId);
        const session2 = sessions.find((s) => s.session_id === defaultOption2.sessionId);

        if (!selectedOption1) {
          setSelectedOption1(defaultOption1.value);
          setsessionId1(defaultOption1.sessionId);
          setLaps1(session1?.lap_times || []);
          setSessionLapId1(session1?.best_lap_idx || 0);
        }
        if (!selectedOption2) {
          setSelectedOption2(defaultOption2.value);
          setsessionId2(defaultOption2.sessionId);
          setLaps2(session2?.lap_times || []);
          setSessionLapId2(session2?.best_lap_idx || 0);
        }
      }
    }
  }, [rowSelection, options1, options2, selectedOption1, selectedOption2]);

  const handleChangeOption1 = (event) => {
    const selectedValue = event.target.value;
    setSelectedOption1(selectedValue);
  
    const selectedObject = options1.find(
      (option) => option.value === selectedValue,
    );
    if (selectedObject) {
      setsessionId1(selectedObject.sessionId);
      const sessionData = dashboardData.sessions.find(
        (s) => s.session_id === selectedObject.sessionId,
      );
      setLaps1(sessionData?.lap_times || []);
      setSessionLapId1(sessionData?.best_lap_idx || 0); // default to best lap
    }
  };

  const handleChangeOption2 = (event) => {
    const selectedValue = event.target.value;
    setSelectedOption2(selectedValue);
  
    const selectedObject = options2.find(
      (option) => option.value === selectedValue,
    );
    if (selectedObject) {
      setsessionId2(selectedObject.sessionId);
      const sessionData = dashboardData.sessions.find(
        (s) => s.session_id === selectedObject.sessionId,
      );
      setLaps2(sessionData?.lap_times || []);
      setSessionLapId2(sessionData?.best_lap_idx || 0); // default to best lap
    }
  };

  const handleChangeLap1 = (event) => {
    setSessionLapId1(parseInt(event.target.value));
  };
  
  const handleChangeLap2 = (event) => {
    setSessionLapId2(parseInt(event.target.value));
  };

  const onUpload = async () => {
    if (isGeneratingReport) {
      toast.error("Please wait until the current report is generated", {
        position: "top-center",
        duration: 1500,
      });
      return;
    }
    dispatch(setIsGeneratingReport(true));

    const loadingToastId = toast.loading("Generating report...", {
      position: "top-center",
    });
    try {
      console.log(sessionId1, sessionLapId1, sessionId2, sessionLapId2)
      await generateReport(sessionId1, sessionLapId1, sessionId2, sessionLapId2);
      toast.dismiss(loadingToastId);
      toast.success("Report generated successfully!", {
        position: "top-center",
        duration: 3000,
      });
      dispatch(fetchDashboardData());
    } catch (error) {
      toast.dismiss(loadingToastId);
      toast.error("Failed to generate report.", {
        position: "top-center",
        duration: 3000,
      });
    }
    dispatch(setIsGeneratingReport(false));
    onClose();
  };

  return (
    <div className="popup">
      <div className="popup-content generate-report">
        <div className="tit-popup">
          <h2>Generate report</h2>
        </div>
        <div>
          <div className="lap-selector">
            <div className="select-container">
              <span>Session</span>
              <select value={selectedOption1} onChange={handleChangeOption1}>
                {options1.map((option) => (
                  <option key={option.key} value={option.value}>
                    {option.value}
                  </option>
                ))}
              </select>
            </div>
            <div className="select-lap-time">{/* "select-lap-time"> */}
              <span>Lap</span>
              <select value={sessionLapId1 ?? 0} onChange={handleChangeLap1}>
                {computedLaps1.map((lap) => (
                  <option
                    key={lap.value}
                    value={lap.value}
                    disabled={!lap.isValid}
                    title={!lap.isValid ? "Invalid" : null}
                    style={lap.isBest ? { fontWeight: 900 } : {}}
                  >
                    {lap.label} {lap.isBest && sessionLapId1 !== lap.value ? "(Best)" : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="custom-select">
            <h3>Parameters</h3>
            <div className="lap-selector">
              <div className="select-container">
                <span>Reference</span>
                <select value={selectedOption2} onChange={handleChangeOption2}>
                  {options2.map((option2) => (
                    <option key={option2.key} value={option2.value}>
                      {option2.value}
                    </option>
                  ))}
                </select>
              </div>
              <div className="select-lap-time">
                <span>Lap</span>
                <select value={sessionLapId2 ?? 0} onChange={handleChangeLap2}>
                  {computedLaps2.map((lap) => (
                    <option
                      key={lap.value}
                      value={lap.value}
                      disabled={!lap.isValid}
                      title={!lap.isValid ? "Invalid" : null}
                      style={lap.isBest ? { fontWeight: 900 } : {}}
                    >
                      {lap.label} {lap.isBest && sessionLapId2 !== lap.value ? "(Best)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="cont-btn">
          <button className="btn btn-blue" onClick={onUpload}>
            Generate
          </button>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupReport;
